/**
 * The panel's dimensions, shared by every step that draws the phrase keyboard.
 *
 * Not a cosmetic constant. S0, S1a and the practice runs draw the same
 * keyboard over the same window, so a key has to land on the same pixel from
 * one screen to the next — which only holds if they agree on the width, the
 * padding, and therefore on how large the drawing may be. Kept here rather
 * than duplicated per screen: a copy is a convention, and a convention drifts
 * at the first change.
 */

export const PANEL_WIDTH_PX = 560;
export const PANEL_PADDING_X_PX = 28;

/** Inset of the floating shell, the band and the phrase chip from the
 *  container edge (px). */
export const SHELL_INSET_PX = 16;

/**
 * Height of the phrase chip's row at the top of the roll: its own height
 * (6 px padding, an 11 px line at 1.2, 1 px borders — 27 px) plus a 13 px
 * gap under it.
 *
 * The centred capsule starts **below** this row. Any corner the chip took
 * on its own ended up under the capsule: top-left as soon as the roll was
 * narrower than the two side by side, bottom-left as soon as the capsule
 * was tall enough to reach the keys — and it is, on S0 at a laptop height.
 * A row of its own is the only placement that holds for every width and
 * every panel height, and it costs the capsule 40 px it can spare.
 */
export const PHRASE_CHIP_ROW_PX = 40;

/** Width the keyboard may not exceed inside the panel. */
export const KEYBOARD_MAX_W_PX = PANEL_WIDTH_PX - PANEL_PADDING_X_PX * 2;

/** Height the practice keyboard takes when nothing else bounds it. */
export const KEYBOARD_H_PX = 104;

/**
 * The accent of a screen where both hands are on stage.
 *
 * Achromatic on purpose, and it is not a nicety. Anything tinted on these
 * screens claims a hand: `--color-accent` is `#8b5cf6`, twelve degrees of hue
 * from `--color-hand-right`, and the teal that used to paint "run validé" is
 * one degree from `--color-hand-left`. A progress bar or a trace in either of
 * them is painted in what the legend just above defines as a hand.
 *
 * The chrome, then — rails, traces, progress. The one act the player is meant
 * to press is `ACT_FILL`, which is the exception and says why.
 *
 * Shared rather than declared per screen: a copy is a convention, and a
 * convention drifts at the first change.
 */
export const NEUTRAL_ACCENT = "var(--color-text-secondary)";

/**
 * The fill of a filled act — the primary button of an end-of-run card, and
 * the button that starts a pass before there is one.
 *
 * Tinted where the chrome around it is not, which is the whole point: on a
 * two-hand screen the achromatic rule left the card filled in white or grey,
 * one screen after the practice step fills the same two words in the colour
 * of the hand. Same card, same act, another product. The act is not chrome —
 * nobody reads a button as the legend of a rail — so it takes the parcours'
 * own violet rather than either hand's.
 */
export const ACT_FILL = "var(--color-accent)";

/**
 * The achromatic fill scale the run-in-time screens draw their traces with.
 *
 * `globals.css` has no such scale — only `--color-border-subtle` (0.06) and
 * `--color-glass-separator` (0.10) — and these four levels are specific to
 * the journey, so they live next to the panel they serve rather than in the
 * global sheet.
 *
 * Contrasts are against the panel's own ground (`--color-glass-shell`
 * composited ≈ #0d0d12, L = 0.0042):
 *
 * | token        | alpha | ratio   | what it carries                     |
 * |--------------|-------|---------|-------------------------------------|
 * | `TRACE_OFF`  | 0.06  | 1.08:1  | empty — carries nothing on its own   |
 * | `TRACE_EDGE` | 0.34  | 3.06:1  | the boundary of an empty cell, rules |
 * | `TRACE_INK`  | 0.42  | 4.00:1  | read and held                        |
 * | `TRACE_SEL`  | 0.72  | 8.90:1  | the selection ring                   |
 * | `TRACE_MARK` | 0.92  | 16.4:1  | a stop, the position marker          |
 *
 * **Three mutually-distinguishable achromatic fills is the ceiling** on this
 * ground: 0.06 / 0.42 / 0.92 sit at 3.6, 4.0 and 14.4 to one another, and a
 * fourth would fall under the 3:1 WCAG 1.4.11 asks of a meaningful graphic.
 * A fourth state goes through **form** — hollow versus solid, as `RunMap`
 * already does — never through a fourth grey.
 */
export const TRACE_OFF = "rgba(255, 255, 255, 0.06)";
export const TRACE_EDGE = "rgba(255, 255, 255, 0.34)";
export const TRACE_INK = "rgba(255, 255, 255, 0.42)";
export const TRACE_SEL = "rgba(255, 255, 255, 0.72)";
export const TRACE_MARK = "rgba(255, 255, 255, 0.92)";

/**
 * How a secondary offer says it is one.
 *
 * These lines — « Un peu plus vite », « Revenir aux notes », « Jouer sans le
 * métronome » — were plain secondary text, at the same size, weight and
 * colour as the meta line sitting right above them. Nothing said they could
 * be pressed, and under a white primary button they read as a caption.
 *
 * The mark is a **dashed underline**, not a colour: the two hands own the
 * only hues on this screen, and the panel is already at its ceiling of three
 * mutually-legible achromatic fills. It is also the idiom the product uses
 * for an inline action elsewhere (the studio's empty screen), transposed to
 * a neutral ground. On hover the whole thing goes to primary, underline
 * included, so the affordance confirms itself.
 */
export const OFFER_STYLE = {
  marginLeft: -4,
  padding: "2px 4px 1px",
  borderRadius: 4,
  border: "none",
  borderBottom: `1px dashed ${TRACE_EDGE}`,
  background: "transparent",
  color: "var(--color-text-secondary)",
  fontSize: 12,
  cursor: "pointer",
} as const;

/**
 * The same mark, on a caption line.
 *
 * `OFFER_STYLE` is sized to stand under a primary button — 12 px with a box
 * around it. The rail's legend row is 16 px tall and 11 px, and an offer that
 * lives on it (« Voir ce que tu as joué ») has to fit *inside* the line
 * rather than push it open: same dashed underline, no box, no padding.
 */
export const OFFER_INLINE_STYLE = {
  padding: 0,
  border: "none",
  borderBottom: `1px dashed ${TRACE_EDGE}`,
  background: "transparent",
  color: "var(--color-text-secondary)",
  fontSize: 11,
  lineHeight: 1.2,
  cursor: "pointer",
} as const;

/** Hover and focus behaviour that goes with `OFFER_STYLE`. */
export const OFFER_CLASS =
  "transition-colors hover:text-[var(--color-text-primary)] hover:border-b-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]";
